import {createMultiStyleConfigHelpers} from '@chakra-ui/styled-system';

const helpers = createMultiStyleConfigHelpers([
  'root',
  'container',
  'copyRegion',
  'quoteMarkDesktop',
  'quoteMarkMobile',
  'heading',
  'quote',
  'quoteContent',
  'quoteMarkup',
  'logosRegion',
  'logoItem',
]);

/** Shared absolute watermark styles for the quote mark SVG. */
const quoteMarkBase = {
  color: 'brandColors.greyscale30',
  pointerEvents: 'none',
  userSelect: 'none',
  lineHeight: 0,
  sx: {
    '& svg': {
      display: 'block',
      width: '100%',
      height: '100%',
    },
  },
};

/**
 * Featured In With Logos (CF-052) theme — copy + award logo row.
 *
 * Quote mark (Figma):
 * - Desktop: large watermark behind the heading (“Award” overlaps the mark)
 * - Mobile: watermark behind the quote body; brown border cuts through the mark
 *
 * @typedef {import('@chakra-ui/styled-system').PartsStyleInterpolation<{
 *  keys: ("root" | "container" | "copyRegion" | "quoteMarkDesktop" | "quoteMarkMobile" | "heading" | "quote" | "quoteContent" | "quoteMarkup" | "logosRegion" | "logoItem")[]
 * }>} FeaturedInWithLogosCf052Styles
 */
export const featuredInWithLogosCf052Styles = helpers.defineMultiStyleConfig({
  /**
   * @returns {FeaturedInWithLogosCf052Styles}
   */
  baseStyle() {
    return {
      root: {
        width: '100%',
        overflow: 'visible',
      },
      container: {
        maxWidth: '1440px',
        marginX: 'auto',
        paddingTop: {base: 14, md: 20},
        paddingBottom: {base: 14, md: 20},
        paddingLeft: {base: 5, md: 20},
        paddingRight: {base: 5, md: 20},
        flexDirection: {base: 'column', md: 'row'},
        alignItems: {base: 'flex-start', md: 'center'},
        justifyContent: 'space-between',
        gap: {base: 12, md: 16},
        overflow: 'visible',
      },
      copyRegion: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        flex: {base: '1', md: '1 1 55%'},
        maxWidth: {base: '100%', md: '640px'},
        paddingTop: {base: 0, md: 6},
        overflow: 'visible',
      },
      /**
       * Desktop — absolute on copyRegion, behind heading.
       * Hung left so the mark sits mostly before “Award”.
       */
      quoteMarkDesktop: {
        ...quoteMarkBase,
        display: {base: 'none', md: 'block'},
        position: 'absolute',
        top: {md: '-20px'},
        left: {md: '-36px'},
        width: {md: '104px'},
        height: {md: '118px'},
        zIndex: 0,
      },
      /**
       * Mobile — absolute on quote box, behind border + body copy.
       * Border (left edge of quote) bisects the left half of the mark.
       */
      quoteMarkMobile: {
        ...quoteMarkBase,
        display: {base: 'block', md: 'none'},
        position: 'absolute',
        top: {base: '-30px'},
        left: {base: '-25px'},
        width: {base: '72px'},
        height: {base: '84px'},
        zIndex: 0,
      },
      heading: {
        position: 'relative',
        zIndex: 1,
        marginBottom: {base: '50px', md: 5},
        fontFamily: 'heading',
        fontStyle: 'normal',
        fontWeight: 500,
        fontSize: '1.5rem',
        lineHeight: '120%',
        letterSpacing: {base: 'headingTight', md: 'headingTighter'},
        color: 'brandColors.greyscale90',
      },
      quote: {
        position: 'relative',
        zIndex: 1,
        borderLeftWidth: '2px',
        borderLeftStyle: 'solid',
        borderLeftColor: 'primary.darkLinen',
        paddingLeft: {base: 4, md: 5},
        // Extra breathing room before logos on mobile.
        marginBottom: {base: 2, md: 0},
        width: '100%',
        overflow: 'visible',
      },
      quoteContent: {
        position: 'relative',
        zIndex: 1,
      },
      quoteMarkup: {
        sx: {
          '.amb-markup p': {
            fontStyle: 'normal',
            fontWeight: 400,
            fontSize: '1.27rem',
            lineHeight: {base: '160%', md: '160%'},
            color: 'brandColors.greyscale90',
            marginBottom: 0,
          },
          '.amb-markup p + p': {
            marginTop: 2,
          },
        },
      },
      logosRegion: {
        flex: {base: '1', md: '0 0 auto'},
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: {base: 'flex-start', md: 'flex-end'},
        gap: {base: 8, md: 12},
        marginTop: {base: 2, md: 0},
      },
      logoItem: {
        flexShrink: 0,
        sx: {
          '.icon__title, .icon__description': {
            display: 'none',
          },
          '.icon, .icon__link': {
            alignItems: 'center',
          },
          '.icon__image': {
            width: 'auto',
            height: {base: '96px', md: '120px'},
            maxWidth: {base: '160px', md: '200px'},
            objectFit: 'contain',
          },
          '.icon__image svg': {
            width: 'auto',
            height: {base: '96px', md: '120px'},
            maxWidth: {base: '160px', md: '200px'},
          },
        },
      },
    };
  },
});
